/**
 * SRES · WizardMainShell (Sprint P13 — Feb 2026)
 * ────────────────────────────────────────────────
 * The single top-level view for every project. Replaces the classic
 * tab bar with an 11-step vertical wizard flow ending at CPR Generation.
 *
 * ▸ Left side  — vertical step nav (numbered, current-step highlighted,
 *                completed steps get a green tick, locked steps get a
 *                lock icon and are unclickable).
 * ▸ Right side — the active step's body (auto-runs heavy backend calcs
 *                on entry so users never see a "Compute" button).
 *
 * Steps are locked until the previous step reports `complete=true` with
 * zero critical errors. Gating rule lives in `WIZARD_STEPS[i].check`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Warning, ArrowLeft, CheckCircle, House, FloppyDisk, CaretUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { getProject, getStats, recordWizardStepTransition } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Sprint P32 · Wizard step config + nav item extracted for maintainability
import { WIZARD_STEPS } from "@/pages/wizard/steps.config";
// Feb 2026 · Iter82 · Client-mandated 9-step linear workflow. The
// wizard shell renders the nine stages in its left rail (grouping
// legacy sub-step ids into compound stages).
import { WORKFLOW_STAGES, resolveStageForWizardStep } from "@/config/simplified_flow";
import {
  markStageComplete,
} from "@/lib/stageActuals";






// ---------------------------------------------------------------------
// Main shell
// ---------------------------------------------------------------------
export default function WizardMainShell() {
  const { pid, stepId } = useParams();
  const navigate = useNavigate();

  // Iter112 · Landscape mobile optimisation. When the viewport height
  // is < 500 px AND width > height (phone in landscape), the fixed
  // footer action bar collapses behind a floating chevron so the step
  // body reclaims the missing vertical real estate. Users tap the
  // chevron to expose the buttons on demand.
  const [footerOpen, setFooterOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const short = window.innerHeight < 500 && window.innerWidth > window.innerHeight;
    return !short;
  });
  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const handler = () => {
      const short = window.innerHeight < 500 && window.innerWidth > window.innerHeight;
      // Only auto-collapse when the viewport transitions INTO short
      // landscape — never auto-expand once the user has manually
      // collapsed. `footerOpen` is left untouched otherwise.
      setFooterOpen((prev) => (short ? false : prev));
    };
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, []);

  const projectQ = useQuery({
    queryKey: ["wizard-project", pid],
    queryFn: () => getProject(pid),
    enabled: !!pid,
    staleTime: 15_000,
  });
  const statsQ = useQuery({
    queryKey: ["wizard-stats", pid],
    queryFn: () => getStats(pid),
    enabled: !!pid,
    refetchInterval: 20_000,
    staleTime: 5_000,
  });

  // Extra flags the check-fns rely on (validation errors, block-model presence).
  const [extras, setExtras] = useState({});

  useEffect(() => {
    if (!pid) return;
    // Fetch validation errors + block-model existence in the background.
    (async () => {
      try {
        const val = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/validation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("sres_token") || ""}` },
        }).then((r) => r.ok ? r.json() : null);
        const nErr = val
          ? [...(val.errors || []), ...(val.critical || [])].length
          : 0;
        setExtras((s) => ({ ...s, __validation: { n_errors: nErr } }));
      } catch (e) { /* ignore */ }
      try {
        const bm = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/block_model`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("sres_token") || ""}` },
        }).then((r) => r.ok ? r.json() : null);
        setExtras((s) => ({ ...s, __has_bm: !!(bm && bm.ore_blocks) }));
      } catch (e) { /* ignore */ }
    })();
  }, [pid, statsQ.dataUpdatedAt]);

  const project = projectQ.data;
  const stats = useMemo(
    () => ({ ...(statsQ.data || {}), ...extras }),
    [statsQ.data, extras],
  );
  const checks = useMemo(
    () => WIZARD_STEPS.map((s) => s.check(project, stats)),
    [project, stats],
  );

  // Current step by URL param — default to step 1 if none supplied.
  const currentIdx = useMemo(() => {
    if (!stepId) return 0;
    const i = WIZARD_STEPS.findIndex((s) => s.id === stepId);
    return i === -1 ? 0 : i;
  }, [stepId]);
  const current = WIZARD_STEPS[currentIdx];

  // ── Step-transition analytics (Sprint P13 wrap-up) ─────────────────────
  // Emit an event to `db.audit_log` every time the resolved step id
  // changes. `useRef` tracks the previous step so we can attribute the
  // transition to `next` / `prev` / `nav`. Best-effort — silently
  // swallows network errors so a fetch blip never breaks the wizard.
  const lastStepRef = useRef({ id: null, ts: Date.now() });
  useEffect(() => {
    if (!pid || !current?.id) return;
    const prev = lastStepRef.current.id;
    if (prev === current.id) return;
    // Deep-link entry (no prior client-side navigation this session).
    let reason = "nav";
    if (prev === null) {
      reason = new URLSearchParams(window.location.search).get("resume")
        ? "resume" : "deep_link";
    } else {
      const prevIdx = WIZARD_STEPS.findIndex((s) => s.id === prev);
      const nowIdx  = currentIdx;
      if (nowIdx === prevIdx + 1) reason = "next";
      else if (nowIdx === prevIdx - 1) reason = "prev";
      else reason = "nav";
    }
    recordWizardStepTransition(pid, {
      from_step: prev, to_step: current.id, reason,
    }).catch(() => { /* silent */ });
    lastStepRef.current = { id: current.id, ts: Date.now() };
  }, [pid, current?.id, currentIdx]);

  // Iter82 · Feb 2026 · Personal-progress tracking runs globally at
  // AppShell → <StageTracker />. Here we only mark stages complete
  // when their sub-step checks flip green — the shared sidebar
  // (`StagesSidebar`) polls localStorage independently.
  useEffect(() => {
    if (!pid) return;
    for (const stage of WORKFLOW_STAGES) {
      const steps = WIZARD_STEPS
        .map((s, i) => ({ ...s, idx: i }))
        .filter((s) => (stage.wizardStepIds || []).includes(s.id));
      if (steps.length > 0 && steps.every((s) => checks[s.idx]?.complete)) {
        markStageComplete(pid, stage.key);
      }
    }
  }, [pid, checks]);

  const isLocked = (i) => {
    // Sprint A: relax gating so users can hop across the 20-step flow
    // for review purposes. Blocking messages are still shown in the
    // footer but do not prevent navigation. Sprint E will layer in
    // hard gates only around approval / lock-for-publication actions.
    return false;
  };

  const goTo = (i) => {
    // Priority is: use the step's external route (opens the dedicated
    // page like GIS Studio) when defined; otherwise stay inside the
    // wizard shell.
    const step = WIZARD_STEPS[i];
    if (step?.external_route) {
      const url = step.external_route(pid);
      if (url) return navigate(url);
    }
    navigate(`/app/projects/${pid}/wizard/${step.id}`, { replace: false });
  };

  const goNext = () => {
    const c = checks[currentIdx];
    if (c.blocking) {
      toast.error(c.message || "Resolve blocking issues before advancing");
      return;
    }
    // Iter82 · Feb 2026 · Client mandate — the wizard footer must
    // advance by STAGE, not by internal sub-step. If we're on a
    // compound stage (multiple wizard step ids under one stage) the
    // "Save & Continue" click jumps to the FIRST step of the next
    // stage rather than the next sub-step.
    const stage = resolveStageForWizardStep(current?.id);
    const idxInStages = stage
      ? WORKFLOW_STAGES.findIndex((s) => s.key === stage.key)
      : -1;
    if (idxInStages !== -1 && idxInStages + 1 < WORKFLOW_STAGES.length) {
      const nextStage = WORKFLOW_STAGES[idxInStages + 1];
      const url = nextStage.url(pid);
      if (url) return navigate(url);
    }
    // Fallback — advance by internal step (used for legacy sub-step
    // ids that don't belong to any stage yet).
    if (currentIdx + 1 < WIZARD_STEPS.length) goTo(currentIdx + 1);
  };
  const goBack = () => {
    const stage = resolveStageForWizardStep(current?.id);
    const idxInStages = stage
      ? WORKFLOW_STAGES.findIndex((s) => s.key === stage.key)
      : -1;
    if (idxInStages > 0) {
      const prevStage = WORKFLOW_STAGES[idxInStages - 1];
      const url = prevStage.url(pid);
      if (url) return navigate(url);
    }
    if (currentIdx > 0) goTo(currentIdx - 1);
  };

  if (projectQ.isLoading || !project) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm">
        Loading project…
      </div>
    );
  }

  const StepComp = current.component;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50"
              data-testid="wizard-main-shell">
      {/* ── Main body (sidebar is provided by <ProjectStageLayout />) ─ */}
      <main className="flex-1 h-full flex flex-col min-w-0 min-h-0"
             data-testid="wizard-body">
        {/* Step header (fixed) */}
        <header className="shrink-0 bg-white border-b border-slate-200 px-6 py-3
                              flex items-center gap-3" data-testid="wizard-header">
          {(() => {
            const stage = resolveStageForWizardStep(current?.id);
            const stageN = stage?.n || current.n;
            return (
              <div className="h-9 w-9 rounded-md bg-brand-blue text-white
                                  flex items-center justify-center font-bold"
                      title={stage ? `Stage ${stage.n} of ${WORKFLOW_STAGES.length}` : ""}>
                {stageN}
              </div>
            );
          })()}
          <div className="flex-1 min-w-0">
            {(() => {
              const stage = resolveStageForWizardStep(current?.id);
              return stage ? (
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  Stage {stage.n} of {WORKFLOW_STAGES.length} · {stage.label}
                </div>
              ) : null;
            })()}
            <div className="text-base font-bold text-slate-900 truncate"
                  data-testid="wizard-step-title">
              {current.title}
            </div>
            <div className="text-[11px] text-slate-500">{current.subtitle}</div>
          </div>
          {checks[currentIdx].complete
            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                <CheckCircle size={12} className="mr-1" /> Complete
              </Badge>
            : (checks[currentIdx].blocking
                ? <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                    <Warning size={12} className="mr-1" /> Blocking
                  </Badge>
                : <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                    In progress
                  </Badge>)}
        </header>

        {/* Step body (only scrollable region inside the wizard) */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto overflow-x-hidden bg-slate-50"
              data-testid="wizard-step-body">
          <StepComp projectId={pid} project={project} stats={stats}
                     goNext={goNext} goBack={goBack}
                     onDataChanged={() => {
                       statsQ.refetch();
                     }} />
        </div>

        {/* Footer nav (fixed) — Feb 2026 navigation-simplification pass:
            Back · Save · Save & Continue · Next · Return to Dashboard.
            Iter112 · collapsible in short-landscape phone mode. */}
        {footerOpen ? (
          <footer className="shrink-0 bg-white border-t border-slate-200 px-6 py-3
                                  flex items-center gap-2 flex-wrap" data-testid="wizard-footer">
            <Button variant="ghost" size="sm"
                      onClick={() => window.location.assign("/app/dashboard")}
                      data-testid="wizard-return-dashboard"
                      className="text-slate-500 hover:text-brand-blue text-[11.5px] gap-1">
              <House size={12} /> Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={goBack}
                      disabled={currentIdx === 0}
                      data-testid="wizard-prev">
              <ArrowLeft size={12} className="mr-1" /> Back
            </Button>
            <div className="flex-1 text-[11px] text-slate-500 min-w-0 truncate">
              {(() => {
                if (checks[currentIdx].message) return checks[currentIdx].message;
                const stage = resolveStageForWizardStep(current?.id);
                // Iter82 · Feb 2026 · Client mandate — footer surfaces
                // the 9-stage count, never the 20 internal wizard steps.
                return stage
                  ? `Stage ${stage.n} of ${WORKFLOW_STAGES.length} · ${stage.label}`
                  : `Stage — of ${WORKFLOW_STAGES.length}`;
              })()}
            </div>
            <Button variant="outline" size="sm"
                      onClick={() => { statsQ.refetch(); }}
                      data-testid="wizard-save"
                      className="text-[11.5px] gap-1">
              <FloppyDisk size={12} /> Save
            </Button>
            <Button size="sm"
                      onClick={goNext}
                      disabled={checks[currentIdx].blocking
                                   || currentIdx === WIZARD_STEPS.length - 1}
                      data-testid="wizard-save-continue"
                      className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1">
              <FloppyDisk size={12} weight="fill" /> Save & Continue
            </Button>
          </footer>
        ) : (
          <button type="button"
                      onClick={() => setFooterOpen(true)}
                      title="Show action bar"
                      aria-label="Expand wizard action bar"
                      data-testid="wizard-footer-expand"
                      className="fixed right-3 bottom-3 z-30 h-9 pl-2.5 pr-3 rounded-full
                                    bg-brand-blue text-white text-[11.5px] font-semibold
                                    shadow-lg hover:opacity-95 active:scale-[0.97]
                                    flex items-center gap-1.5 transition"
                      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <CaretUp size={13} weight="bold" />
            Actions
          </button>
        )}
      </main>
    </div>
  );
}
